/**
 * Mobile Field Focus Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles field focus management for mobile transaction form:
 * - Field focus state
 * - Auto-focus logic
 * - Field blur handling
 * - Calculator expression sync
 * - Click outside handling
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { formatAmountToCurrencyDecimalPlaces, convertFormattedAmountToRawValue } from "../../../../utils/currencyDefaults";

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

export interface UseMobileFieldFocusParams {
  transactionType: "expense" | "income" | "transfer";
  formData: {
    amount: string;
    accountId: string;
    category: string;
    currency?: string;
    rate?: string;
    defaultCurrencyRate?: string;
    details?: Record<string, string | number>;
  };
  transferFormData: {
    amount: string;
    fromAccountId?: string;
    feeAmount?: string;
    feeAccountId?: string;
  };
  calculatorExpression: string;
  hasEvaluatedRef: React.MutableRefObject<boolean>;
  setCalculatorExpression: (value: string) => void;
  setFormData: (updater: (prev: any) => any) => void;
  setTransferFormData: (updater: (prev: any) => any) => void;
  setRateManuallyEdited: (value: boolean) => void;
  setDefaultCurrencyRateManuallyEdited: (value: boolean) => void;
  setKeepTimePickerOpen: (value: boolean) => void;
  isOnOptionalField: boolean;
  setIsOnOptionalField: (value: boolean) => void;
  currencies: Array<{ code: string; decimalPlaces?: number }>;
  defaultCurrency: string;
  accounts: Array<{ id: string; currency: string }>;
}

export interface UseMobileFieldFocusResult {
  focusedField: FocusedField;
  setFocusedField: React.Dispatch<React.SetStateAction<FocusedField>>;
  handleFieldFocus: (field: FocusedField) => void;
  handleFieldBlur: (field: FocusedField) => void;
  bottomPanelRef: React.RefObject<HTMLDivElement | null>;
  timePickerRef: React.RefObject<HTMLDivElement | null>;
  amountInputRef: React.RefObject<HTMLInputElement | null>;
  isInitialFocus: boolean;
  setIsInitialFocus: (value: boolean) => void;
}

/**
 * Hook for managing field focus in mobile transaction form
 */
export const useMobileFieldFocus = ({
  transactionType,
  formData,
  transferFormData,
  calculatorExpression,
  hasEvaluatedRef,
  setCalculatorExpression,
  setFormData,
  setTransferFormData,
  setRateManuallyEdited,
  setDefaultCurrencyRateManuallyEdited,
  setKeepTimePickerOpen,
  isOnOptionalField,
  setIsOnOptionalField,
  currencies,
  defaultCurrency,
  accounts,
}: UseMobileFieldFocusParams): UseMobileFieldFocusResult => {
  const [focusedField, setFocusedField] = useState<FocusedField>(null);
  const [isInitialFocus, setIsInitialFocus] = useState<boolean>(true);
  const bottomPanelRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle field focus
  const handleFieldFocus = useCallback(
    (field: FocusedField) => {
      // Clear any pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Save calculator expression when switching away from amount, rate, defaultCurrencyRate, feeAmount, or measurable detail field
      // Skip if we've already evaluated and saved (from check button)
      const isCalculatorField = focusedField === "amount" || focusedField === "rate" || focusedField === "defaultCurrencyRate" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"));
      if (isCalculatorField && field !== focusedField && !hasEvaluatedRef.current) {
        // Remove trailing operators before saving
        let finalValue = calculatorExpression.replace(/[+\-*/]$/, "");
        // For amount and feeAmount fields, keep "0" as "0" (don't convert to empty string)
        // For rate fields, convert "0" to empty string (as before)
        const cleanValue = ((focusedField === "amount" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"))) && finalValue === "0") ? "0" : (finalValue === "0" ? "" : finalValue);

        // Save to form based on which field was focused
        if (typeof focusedField === "string" && focusedField.startsWith("detail:")) {
          const detailName = focusedField.slice(7);
          const toStore = cleanValue === "" || cleanValue === "0" ? "" : (parseFloat(cleanValue) || 0);
          setFormData((prev) => ({ ...prev, details: { ...(prev.details || {}), [detailName]: toStore } }));
        } else if (focusedField === "amount") {
          // Format amount based on currency decimal places (like check button does)
          // If value is "0", keep it as "0" (not empty)
          let formattedAmount = cleanValue === "0" ? "0" : cleanValue;
          if (cleanValue && cleanValue.trim() !== "" && cleanValue !== "0") {
            if (transactionType === "transfer") {
              // Get currency from fromAccount
              const fromAccount = transferFormData.fromAccountId 
                ? accounts.find((acc) => acc.id === transferFormData.fromAccountId)
                : null;
              const transferCurrency = fromAccount?.currency || defaultCurrency;
              formattedAmount = formatAmountToCurrencyDecimalPlaces(
                cleanValue,
                transferCurrency,
                currencies,
                defaultCurrency
              );
            } else {
              // Get currency from formData or account
              const currentCurrency = formData.currency || 
                (formData.accountId ? accounts.find((acc) => acc.id === formData.accountId)?.currency : defaultCurrency) || 
                defaultCurrency;
              formattedAmount = formatAmountToCurrencyDecimalPlaces(
                cleanValue,
                currentCurrency,
                currencies,
                defaultCurrency
              );
            }
          }
          
          if (transactionType === "transfer") {
            setTransferFormData((prev) => ({ ...prev, amount: formattedAmount }));
          } else {
            setFormData((prev) => ({ ...prev, amount: formattedAmount }));
          }
        } else if (focusedField === "rate") {
          setFormData((prev) => ({ ...prev, rate: cleanValue }));
          // Only mark as manually edited if the value actually changed (so refresh button stays disabled when user left without editing)
          const prevRate = (formData.rate ?? "").trim();
          const newRate = (cleanValue ?? "").trim();
          const bothEmptyOrZero = (prevRate === "" || prevRate === "0") && (newRate === "" || newRate === "0");
          const sameNumeric = prevRate !== "" && prevRate !== "0" && newRate !== "" && newRate !== "0" && Math.abs(parseFloat(prevRate) - parseFloat(newRate)) < 1e-9;
          // If user cleared the rate (empty string), mark as manually edited so auto-populate doesn't restore it
          // But refresh button will still work to restore the calculated rate
          if (newRate === "" && prevRate !== "") {
            setRateManuallyEdited(true);
          } else if (!bothEmptyOrZero && !sameNumeric) {
            setRateManuallyEdited(true);
          }
        } else if (focusedField === "defaultCurrencyRate") {
          setFormData((prev) => ({ ...prev, defaultCurrencyRate: cleanValue }));
          // Always mark as manually edited when user enters a value
          // This ensures the entered value is used directly without multiplication
          if (cleanValue && cleanValue.trim() !== "" && cleanValue.trim() !== "0") {
            setDefaultCurrencyRateManuallyEdited(true);
          }
        } else if (focusedField === "feeAmount") {
          // Format fee amount based on fee account currency decimal places
          let formattedFeeAmount = cleanValue;
          if (cleanValue && cleanValue.trim() !== "") {
            // Get currency from feeAccount
            const feeAccount = transferFormData.feeAccountId 
              ? accounts.find((acc) => acc.id === transferFormData.feeAccountId)
              : null;
            const feeCurrency = feeAccount?.currency || defaultCurrency;
            formattedFeeAmount = formatAmountToCurrencyDecimalPlaces(
              cleanValue,
              feeCurrency,
              currencies,
              defaultCurrency
            );
          }
          setTransferFormData((prev) => ({ ...prev, feeAmount: formattedFeeAmount }));
        }
      }
      // Reset the flag after switching fields
      if (isCalculatorField && field !== focusedField) {
        hasEvaluatedRef.current = false;
      }

      // Initialize calculator expression when focusing on amount or measurable detail field
      if (field === "amount" && focusedField !== "amount") {
        const currentAmount = transactionType === "transfer" ? transferFormData.amount : formData.amount;
        // Convert formatted amount to raw value (like onClick handler does)
        const rawValue = currentAmount && currentAmount.trim() !== "" 
          ? convertFormattedAmountToRawValue(currentAmount) 
          : "0";
        // Always set calculator to the amount value when switching from another field (e.g. rate).
        // Otherwise the amount field would show the previous field's value (e.g. rate) until the user types.
        setCalculatorExpression(rawValue);
      }
      if (typeof field === "string" && field.startsWith("detail:") && focusedField !== field) {
        const detailName = field.slice(7);
        const formDetails = formData.details as Record<string, string | number> | undefined;
        const v = formDetails?.[detailName];
        const raw = v === undefined || v === null || v === "" ? "0" : String(v);
        setCalculatorExpression(raw);
      }

      // Set keepTimePickerOpen when time picker opens
      if (field === "time") {
        setKeepTimePickerOpen(true);
      }

      // When user focuses a main field (amount, account, etc.), clear optional-field flag so we're no longer "in attachment section"
      if (field != null) {
        setIsOnOptionalField(false);
      }
      setFocusedField(field);
    },
    [
      focusedField,
      calculatorExpression,
      hasEvaluatedRef,
      transactionType,
      setFormData,
      setTransferFormData,
      setRateManuallyEdited,
      setKeepTimePickerOpen,
      setIsOnOptionalField,
      currencies,
      defaultCurrency,
      accounts,
      transferFormData.fromAccountId,
      transferFormData.amount,
      formData.currency,
      formData.accountId,
      formData.amount,
    ]
  );

  const handleFieldBlur = useCallback(
    (field: FocusedField) => {
      // Only handle blur for fields that should close on blur (not date/amount/time/rate/defaultCurrencyRate/feeAmount)
      if (field === "date" || field === "amount" || field === "time" || field === "rate" || field === "defaultCurrencyRate" || field === "feeAmount") {
        return; // Don't close date/amount/time/rate/feeAmount panels on blur
      }

      // Delay blur to allow click events on bottom options
      blurTimeoutRef.current = setTimeout(() => {
        // Check if user clicked inside the bottom panel
        const activeElement = document.activeElement;
        if (bottomPanelRef.current && bottomPanelRef.current.contains(activeElement)) {
          return; // Don't close if clicking inside panel
        }
        setFocusedField(null);
        blurTimeoutRef.current = null;
      }, 300);
    },
    []
  );

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (focusedField) {
        const target = event.target as Node;
        // When any field card is open (amount/calculator, date, time, account, etc.), tapping a different field should close the current one and open the new field
        const fieldEl = target instanceof HTMLElement ? target.closest?.("[data-field]") : null;
        if (fieldEl) {
          const tappedField = fieldEl.getAttribute("data-field") as FocusedField | null;
          if (tappedField && tappedField !== focusedField) {
            handleFieldFocus(tappedField);
            return;
          }
        }
        // Check if clicking inside bottom panel or time picker
        const isInsidePanel = bottomPanelRef.current?.contains(target);
        const isInsideTimePicker = timePickerRef.current?.contains(target);

        // Don't close if clicking inside the panel, time picker, calculator, or on the input field
        const isInsideCalculator = target instanceof HTMLElement && target.closest('[role="dialog"]') && target.closest('[data-calculator]');
        if (!isInsidePanel && !isInsideTimePicker && !isInsideCalculator && !(target instanceof HTMLElement && target.closest("input"))) {
          // Only close if clicking outside both panel and input fields
          const isInputField = target instanceof HTMLElement && (target.tagName === "INPUT" || target.closest("input"));
          if (!isInputField) {
            // Don't close date/time/amount/rate/defaultCurrencyRate/feeAmount/measurable-detail on outside click (they have their own Done buttons / calculator)
            if (focusedField === "date" || focusedField === "time" || focusedField === "amount" || focusedField === "rate" || focusedField === "defaultCurrencyRate" || focusedField === "feeAmount" || (typeof focusedField === "string" && focusedField.startsWith("detail:"))) {
              return;
            }
            // Close other fields when clicking outside
            setFocusedField(null);
          }
        }
      }
    };

    if (focusedField) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [focusedField, calculatorExpression, transactionType, transferFormData, formData, handleFieldFocus]);

  // Auto-focus amount field when component loads and data is ready
  // This will be handled by the main component after data is loaded

  // Auto-open account or category selector when amount is entered
  useEffect(() => {
    // Only for non-transfer transactions
    if (transactionType === "transfer") return;

    // Only if amount field is not currently focused (user finished entering amount)
    if (focusedField === "amount") return;

    // Don't trigger if account or category is already focused (checkmark button might have opened it)
    if (focusedField === "account" || focusedField === "category") return;

    // Don't auto-focus if user is currently interacting with optional fields (note/attachment)
    if (isOnOptionalField) {
      return; // Don't auto-focus when user is typing in note or attachment
    }

    // Check if amount is filled (allow "0" as valid amount)
    const currentAmount = formData.amount;
    const hasAmount = currentAmount && currentAmount.trim() !== ""; // Allow "0" as valid amount
    const hasAccount = formData.accountId && formData.accountId.trim() !== "";
    const hasCategory = formData.category && formData.category.trim() !== "";

    // Auto-open account or category if amount is filled (including "0")
    // This handles cases where user switches away from amount field without clicking checkmark
    // BUT only if user is not currently interacting with optional fields (note/attachment)
    if (hasAmount && focusedField === null) {
      // Small delay to ensure smooth transition and avoid conflicts with checkmark button
      const timer = setTimeout(() => {
        // Double-check the state and that user is not on note/attachment
        if (focusedField === null && !isOnOptionalField) {
          if (!formData.accountId) {
            // Account is not selected, go to account field
            handleFieldFocus("account");
          } else if (!formData.category) {
            // Account is selected but category is not, go to category
            handleFieldFocus("category");
          }
        }
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [formData.amount, formData.accountId, formData.category, transactionType, focusedField, isOnOptionalField, handleFieldFocus]);

  return {
    focusedField,
    setFocusedField,
    handleFieldFocus,
    handleFieldBlur,
    bottomPanelRef,
    timePickerRef,
    amountInputRef,
    isInitialFocus,
    setIsInitialFocus,
  };
};
